import { Controller } from "@hotwired/stimulus"

const MAX_FILE_SIZE = 10 * 1024 * 1024

export default class extends Controller {
  static targets = [
    "dropzone", "fileInput", "fileInfo", "fileName", "submitButton",
    "uploadCard",
    "loading", "results", "resultsContent", "error", "errorText",
    "statusText"
  ]

  static values = {
    analyseUrl: String,
    pendingPollUrl: String
  }

  connect() {
    if (this.hasPendingPollUrlValue && this.pendingPollUrlValue) {
      this._setLoading(true)
      this._pollForCompletion(this.pendingPollUrlValue)
    }
  }

  openFilePicker() {
    this.fileInputTarget.click()
  }

  selectFile(event) {
    const file = event.target.files[0]
    if (file) this._setFile(file)
  }

  handleDragOver(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.add("border-cyan-500", "bg-cyan-50")
  }

  handleDragLeave(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.remove("border-cyan-500", "bg-cyan-50")
  }

  handleDrop(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.remove("border-cyan-500", "bg-cyan-50")
    const file = event.dataTransfer.files[0]
    if (file) this._setFile(file)
  }

  clearFile() {
    this.selectedFile = null
    this.fileInputTarget.value = ""
    this.fileInfoTarget.classList.add("hidden")
    this._hideError()
  }

  async analyse() {
    if (!this.selectedFile) return
    if (this._polling) return

    this._setLoading(true)

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      const formData = new FormData()
      formData.append("cv_file", this.selectedFile)

      const response = await fetch(this.analyseUrlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json"
        },
        body: formData
      })

      if (response.ok) {
        const data = await response.json()
        if (data.status === "processing" && data.poll_url) {
          this._pollForCompletion(data.poll_url)
        } else {
          this._renderResults(data)
          this._showCompleted()
          this._scrollToResults()
        }
      } else if (response.status === 401) {
        window.location.href = "/users/sign_in"
      } else if (response.status === 403) {
        this._showError("You've reached your CV analysis limit for this month. Upgrade for more.")
      } else {
        const data = await response.json().catch(() => ({}))
        this._showError(data.error || "Unable to analyse CV. Please try again.")
      }
    } catch {
      this._showError("Network error — please try again.")
    }
  }

  disconnect() {
    this._stopPolling()
  }

  // Private

  _setFile(file) {
    if (file.type !== "application/pdf") {
      this._showError("Only PDF files are accepted.")
      return
    }
    if (file.size > MAX_FILE_SIZE) {
      this._showError("File must be under 10MB.")
      return
    }

    this.selectedFile = file
    this.fileNameTarget.textContent = file.name
    this.fileInfoTarget.classList.remove("hidden")
    this._hideError()
  }

  _pollForCompletion(pollUrl) {
    this._polling = true
    this._pollCount = 0
    const maxAttempts = 120

    this._pollInterval = setInterval(async () => {
      this._pollCount++
      if (this._pollCount > maxAttempts) {
        this._stopPolling()
        this._showError("Analysis timed out. Please try again.")
        return
      }

      this._updateStatus(`Analysing your CV... (${this._pollCount}s)`)

      try {
        const response = await fetch(pollUrl, {
          headers: { Accept: "application/json" }
        })
        const data = await response.json()

        if (data.status === "completed") {
          this._stopPolling()
          this._renderResults(data)
          this._showCompleted()
          this._scrollToResults()
        } else if (data.status === "failed") {
          this._stopPolling()
          this._showError(data.error || "Analysis failed. Please try again.")
        }
      } catch {
        this._stopPolling()
        this._showError("Connection lost. Please try again.")
      }
    }, 1000)
  }

  _stopPolling() {
    this._polling = false
    if (this._pollInterval) {
      clearInterval(this._pollInterval)
      this._pollInterval = null
    }
  }

  _updateStatus(text) {
    if (this.hasStatusTextTarget) this.statusTextTarget.textContent = text
  }

  _setLoading(loading) {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.toggle("hidden", !loading)
    }
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = loading
      this.submitButtonTarget.textContent = loading ? "Analysing..." : "Analyse My CV"
    }
    if (loading && this.hasResultsTarget) {
      this.resultsTarget.classList.add("hidden")
    }
    this._hideError()
  }

  _showError(message) {
    this._setLoading(false)
    if (this.hasErrorTarget && this.hasErrorTextTarget) {
      this.errorTextTarget.textContent = message
      this.errorTarget.classList.remove("hidden")
    }
  }

  _hideError() {
    if (this.hasErrorTarget) {
      this.errorTarget.classList.add("hidden")
    }
  }

  _showCompleted() {
    if (this.hasLoadingTarget) this.loadingTarget.classList.add("hidden")
    if (this.hasUploadCardTarget) {
      this.uploadCardTarget.innerHTML = `
        <div class="text-center py-4">
          <p class="text-sm text-gray-600">Your CV analysis is ready below.</p>
          <p class="text-xs text-gray-400 mt-1">Refresh the page to analyse another CV.</p>
        </div>`
    }
    this._hideError()
  }

  _scrollToResults() {
    if (this.hasResultsTarget) {
      requestAnimationFrame(() => {
        this.resultsTarget.scrollIntoView({ behavior: "smooth", block: "start" })
      })
    }
  }

  _renderResults(data) {
    if (!this.hasResultsTarget || !this.hasResultsContentTarget) return

    const { preview, result } = data
    let html = ""

    // Overall Score
    if (result.overall_score != null) {
      const scoreColor = this._scoreColor(result.overall_score)
      html += `<div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6 text-center">
        <h2 class="text-lg font-semibold text-gray-900 mb-3">Overall CV Score</h2>
        <div class="inline-flex items-center justify-center w-24 h-24 rounded-full border-4 ${scoreColor.border}">
          <span class="text-3xl font-bold ${scoreColor.text}">${result.overall_score}</span>
        </div>
        <p class="text-sm ${scoreColor.text} font-medium mt-2">${scoreColor.label}</p>
      </div>`
    }

    // Summary
    if (result.summary) {
      html += `<div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 class="text-lg font-semibold text-gray-900 mb-3">Summary</h2>
        <p class="text-sm text-gray-600">${this._escapeHtml(result.summary)}</p>
      </div>`
    }

    // Section Scores (full only)
    if (!preview && result.section_scores) {
      html += `<div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 class="text-lg font-semibold text-gray-900 mb-4">Section Scores</h2>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">`
      for (const [section, score] of Object.entries(result.section_scores)) {
        const sc = this._scoreColor(score)
        const label = section.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())
        html += `<div class="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <span class="text-sm text-gray-700">${this._escapeHtml(label)}</span>
          <span class="text-sm font-semibold ${sc.text}">${score}/100</span>
        </div>`
      }
      html += `</div></div>`
    }

    // Strengths (full only)
    if (!preview && result.strengths?.length) {
      html += `<div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 class="text-lg font-semibold text-gray-900 mb-3">Strengths</h2>
        <ul class="space-y-2">`
      result.strengths.forEach(s => {
        html += `<li class="flex items-start gap-2 text-sm text-gray-600">
          <span class="text-green-500 mt-0.5 flex-shrink-0">&#10003;</span>
          ${this._escapeHtml(s)}
        </li>`
      })
      html += `</ul></div>`
    }

    // Improvements
    if (result.improvements?.length) {
      html += `<div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 class="text-lg font-semibold text-gray-900 mb-3">Areas for Improvement</h2>
        <ol class="space-y-3">`
      result.improvements.forEach((item, i) => {
        if (typeof item === "object" && item.area) {
          html += `<li class="pl-4 border-l-2 border-amber-300">
            <p class="text-sm font-medium text-gray-800">${i + 1}. ${this._escapeHtml(item.area)}</p>
            <p class="text-sm text-gray-500 mt-1">${this._escapeHtml(item.action)}</p>
          </li>`
        } else {
          html += `<li class="pl-4 border-l-2 border-amber-300">
            <p class="text-sm text-gray-700">${i + 1}. ${this._escapeHtml(String(item))}</p>
          </li>`
        }
      })
      html += `</ol></div>`
    }

    // NZ-Specific Tips (full only)
    if (!preview && result.nz_specific_tips?.length) {
      html += `<div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 class="text-lg font-semibold text-gray-900 mb-3">NZ-Specific Tips</h2>
        <ul class="space-y-2">`
      result.nz_specific_tips.forEach(tip => {
        html += `<li class="flex items-start gap-2 text-sm text-gray-600">
          <span class="text-cyan-500 mt-0.5 flex-shrink-0">&#9679;</span>
          ${this._escapeHtml(tip)}
        </li>`
      })
      html += `</ul></div>`
    }

    // Preview upgrade CTA
    if (preview) {
      html += `<div class="bg-gradient-to-r from-cyan-50 to-blue-50 border border-cyan-200 rounded-xl p-6 text-center">
        <p class="text-sm font-medium text-gray-700 mb-2">Upgrade to Premium for the full analysis</p>
        <p class="text-xs text-gray-500 mb-3">Includes section-by-section scores, detailed strengths, specific improvements, and NZ-specific tips.</p>
        <a href="/dashboard/subscription" class="inline-block px-4 py-2 text-sm font-medium text-white bg-cyan-600 hover:bg-cyan-700 rounded-lg transition-colors">View Plans</a>
      </div>`
    }

    // Disclaimer
    html += `<p class="text-xs text-gray-400 text-center">Automated analysis for guidance only. Your CV file was automatically deleted after analysis.</p>`

    this.resultsContentTarget.innerHTML = html
    this.resultsTarget.classList.remove("hidden")
  }

  _scoreColor(score) {
    if (score >= 80) return { text: "text-green-600", border: "border-green-400", label: "Excellent" }
    if (score >= 60) return { text: "text-cyan-600", border: "border-cyan-400", label: "Good" }
    if (score >= 40) return { text: "text-amber-600", border: "border-amber-400", label: "Fair" }
    return { text: "text-red-600", border: "border-red-400", label: "Needs Work" }
  }

  _escapeHtml(str) {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }
}
